import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { TimeLogFormTaskSelect } from "@jugl-web/domain-resources/tasks/components/ManageTaskTimeLogForm/components/TimeLogFormTaskSelect";
import { UserRole } from "@jugl-web/rest-api";
import { LogMode } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/LogMode";
import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { TextInput } from "@jugl-web/ui-components/cross-platform/forms/TextInput";
import { cx, useAppVariant, useToast, useTranslations } from "@jugl-web/utils";
import { copyTime } from "@jugl-web/utils/date-time/copyTime";
import { formatSecondsToDuration } from "@jugl-web/utils/date-time/formatSecondsToDuration";
import { usePrevious } from "@jugl-web/utils/hooks/usePrevious";
import { addDays, addSeconds, differenceInSeconds } from "date-fns";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { MAX_NOTES_LENGTH } from "../../consts";
import { useTimeLogActions } from "../../hooks/useTimeLogActions";
import { TaskTimeLogFormDatePicker } from "./components/TaskTimeLogFormDatePicker";
import { MAX_DURATION_SECONDS } from "./consts";
import {
  applyTimeOfDayToDate,
  calculateDuration,
  createDateAtTime,
  createDateWithoutSeconds,
  formatTime,
  formatTimeOfDay,
  getAmPmFromDate,
  parseDuration,
  parseTimeOfDay,
} from "./utils";

export interface FormValues {
  selectedTask: { id: string; name: string } | null;
  from: Date;
  to: Date;
  notes: string;
}

export interface ManageTaskTimeLogFormHandle {
  submit: () => Promise<void>;
  getFormValues: () => FormValues;
}

interface ManageTaskTimeLogFormProps {
  entityId: string;
  meId: string;
  boardId: string | null;
  userRole: UserRole | undefined;
  isTaskSelectable: boolean;
  editingTimeLog?: TaskTimeLogModel;
  initialFormValues?: Partial<FormValues>;
  onAfterSubmit: (timeLog: TaskTimeLogModel) => void;
  onRequestCreateTask?: (formValues?: FormValues) => void;
}

const getDefaultFormValues = (): FormValues => ({
  selectedTask: null,
  from: createDateAtTime(9, 0),
  to: createDateAtTime(17, 0),
  notes: "",
});

export const ManageTaskTimeLogForm = forwardRef<
  ManageTaskTimeLogFormHandle,
  ManageTaskTimeLogFormProps
>(
  (
    {
      entityId,
      meId,
      boardId,
      userRole,
      isTaskSelectable,
      editingTimeLog,
      initialFormValues,
      onAfterSubmit,
      onRequestCreateTask,
    },
    ref
  ) => {
    const {
      control,
      watch,
      handleSubmit,
      reset,
      setValue,
      getValues,
      setError,
      clearErrors,
      formState,
    } = useForm<FormValues>({
      defaultValues: {
        ...getDefaultFormValues(),
        ...initialFormValues,
      },
    });

    const { registerDirtySource } = useNavigationGuardContext();

    const selectedTask = watch("selectedTask");

    const { t } = useTranslations();
    const { toast } = useToast();
    const { isMobile } = useAppVariant();

    const { createTimeLog, updateTimeLog } = useTimeLogActions();

    const fromDate = watch("from");
    const toDate = watch("to");

    const currentAmPm = getAmPmFromDate(fromDate);
    const lastKnownAmPm = usePrevious(currentAmPm) ?? currentAmPm;

    const [fromDisplayValue, setFromDisplayValue] = useState(() =>
      formatTime(fromDate)
    );
    const [toDisplayValue, setToDisplayValue] = useState(() =>
      formatTime(toDate)
    );
    const [totalDisplayValue, setTotalDisplayValue] = useState("00:00:00");

    const restoreDisplayValue = (type: "from" | "to" | "total") => {
      switch (type) {
        case "from":
          setFromDisplayValue(formatTime(getValues("from")));
          break;
        case "to":
          setToDisplayValue(formatTime(getValues("to")));
          break;
        case "total":
          setTotalDisplayValue("00:00:00");
          break;
        default:
          break;
      }
    };

    const handleFromBlur = () => {
      const parsed = parseTimeOfDay(fromDisplayValue, lastKnownAmPm);

      if (!parsed) {
        restoreDisplayValue("from");
        return;
      }

      const currentFrom = getValues("from");
      const currentTo = getValues("to");

      const newFrom = applyTimeOfDayToDate(currentFrom, parsed);
      let newTo = currentTo;

      // If the new "from" time is after the current "to" time on the same day,
      // adjust the "to" date to be the next day to maintain the intended duration
      if (newFrom > currentTo) {
        newTo = addDays(currentTo, 1);
      }

      const newDuration = calculateDuration(newFrom, newTo);

      setValue("from", newFrom, { shouldDirty: true });
      setValue("to", newTo, { shouldDirty: true });
      setFromDisplayValue(formatTimeOfDay(parsed));
      setToDisplayValue(formatTime(newTo));
      setTotalDisplayValue(formatSecondsToDuration(newDuration));
    };

    const handleToBlur = () => {
      const parsed = parseTimeOfDay(toDisplayValue, lastKnownAmPm);

      if (!parsed) {
        restoreDisplayValue("to");
        return;
      }

      const currentFrom = getValues("from");
      let newTo = applyTimeOfDayToDate(currentFrom, parsed);

      if (newTo < currentFrom) {
        newTo = addDays(newTo, 1);
      }

      const newDuration = calculateDuration(currentFrom, newTo);

      setValue("to", newTo, { shouldDirty: true });
      setToDisplayValue(formatTimeOfDay(parsed));
      setTotalDisplayValue(formatSecondsToDuration(newDuration));
    };

    const handleTotalBlur = () => {
      if (!totalDisplayValue.trim()) {
        const currentFrom = getValues("from");
        setValue("to", currentFrom, { shouldDirty: true });
        restoreDisplayValue("total");
        setToDisplayValue(formatTime(currentFrom));
        return;
      }

      const parsed = parseDuration(totalDisplayValue);

      if (!parsed) {
        const currentFrom = getValues("from");
        const currentTo = getValues("to");
        const seconds = Math.max(
          0,
          differenceInSeconds(currentTo, currentFrom)
        );
        setTotalDisplayValue(formatSecondsToDuration(seconds));
        return;
      }

      const cappedSeconds = Math.min(parsed.totalSeconds, MAX_DURATION_SECONDS);

      const currentFrom = getValues("from");
      const newTo = addSeconds(currentFrom, cappedSeconds);

      setValue("to", newTo, { shouldDirty: true });
      setTotalDisplayValue(formatSecondsToDuration(cappedSeconds));
      setToDisplayValue(formatTime(newTo));
    };

    const handleDaySelect = (selectedDate: Date) => {
      const currentFrom = getValues("from");
      const currentTo = getValues("to");
      const duration = Math.min(
        MAX_DURATION_SECONDS,
        Math.max(0, differenceInSeconds(currentTo, currentFrom))
      );

      const newFrom = copyTime(currentFrom, selectedDate);

      setValue("from", newFrom, { shouldDirty: true });
      setValue("to", addSeconds(newFrom, duration), { shouldDirty: true });
    };

    const handleFormSubmit = handleSubmit(async (values) => {
      const duration = calculateDuration(values.from, values.to);

      if (duration === 0) {
        toast(
          t({
            id: "tasks-page.time-log-zero-duration-error",
            defaultMessage: "Duration must be greater than 0",
          }),
          { variant: "error" }
        );
        return;
      }

      if (!values.selectedTask) {
        setError("selectedTask", {
          type: "required",
          message: t({
            id: "tasks-page.select-task-to-proceed",
            defaultMessage: "Select Task to proceed",
          }),
        });
        return;
      }

      const commonModel = {
        startDate: values.from,
        endDate: values.to,
        notes: values.notes,
      };

      if (editingTimeLog) {
        try {
          const updatedTimeLog = await updateTimeLog({
            entityId,
            taskId: values.selectedTask.id,
            timeLogId: editingTimeLog.id,
            model: { ...commonModel, logMode: LogMode.Edited },
          });

          toast(
            t({
              id: "feedback.time-log-update-success",
              defaultMessage: "Time log has been updated",
            })
          );

          onAfterSubmit(updatedTimeLog);
        } catch {
          toast(
            t({
              id: "feedback.time-log-update-error",
              defaultMessage: "Failed to update time log",
            }),
            { variant: "error" }
          );
        }
        return;
      }

      try {
        const createdTimeLog = await createTimeLog({
          entityId,
          taskId: values.selectedTask.id,
          model: { ...commonModel, logMode: LogMode.ManuallyCreated },
        });

        toast(
          t({
            id: "feedback.time-log-create-success",
            defaultMessage: "Time log has been created",
          })
        );

        onAfterSubmit(createdTimeLog);
      } catch {
        toast(
          t({
            id: "feedback.time-log-create-error",
            defaultMessage: "Failed to create time log",
          }),
          { variant: "error" }
        );
      }
    });

    useImperativeHandle(ref, () => ({
      submit: handleFormSubmit,
      getFormValues: () => getValues(),
    }));

    useEffect(() => {
      setFromDisplayValue(formatTime(fromDate));
      setToDisplayValue(formatTime(toDate));

      const seconds = calculateDuration(fromDate, toDate);
      setTotalDisplayValue(formatSecondsToDuration(seconds));
    }, [fromDate, toDate]);

    useEffect(() => {
      if (editingTimeLog) {
        const from = editingTimeLog.startDate;
        const to = editingTimeLog.endDate || createDateWithoutSeconds();
        reset({
          selectedTask: initialFormValues?.selectedTask,
          from,
          to,
          notes: editingTimeLog.notes || "",
        });
      } else {
        reset({
          ...getDefaultFormValues(),
          ...initialFormValues,
        });
      }
    }, [editingTimeLog, initialFormValues, reset]);

    useEffect(() => {
      registerDirtySource("manage-task-log-form", formState.isDirty);
    }, [formState.isDirty, registerDirtySource]);

    return (
      <form className="flex flex-col gap-6" onSubmit={handleFormSubmit}>
        <div className="space-y-2">
          {/* Task selection (not supported on mobile as of now) */}
          {!isMobile && (
            <TimeLogFormTaskSelect
              meId={meId}
              entityId={entityId}
              boardId={boardId}
              userRole={userRole}
              selectedTask={selectedTask}
              isEditable={isTaskSelectable}
              error={formState.errors.selectedTask?.message}
              onSelect={(task) => {
                setValue(
                  "selectedTask",
                  { id: task.id, name: task.name },
                  { shouldDirty: true }
                );
                clearErrors("selectedTask");
              }}
              onRequestCreateTask={() => onRequestCreateTask?.(getValues())}
            />
          )}
          {/* Date picker */}
          <TaskTimeLogFormDatePicker
            fromDate={fromDate}
            toDate={toDate}
            onDaySelect={handleDaySelect}
          />
        </div>
        <div className="flex flex-col gap-6">
          {/* From, to, total */}
          <div className={cx("flex flex-col gap-4", !isMobile && "flex-row")}>
            <TextInput
              label={t({ id: "common.from", defaultMessage: "From" })}
              value={fromDisplayValue}
              onChange={(e) => setFromDisplayValue(e.target.value)}
              onBlur={handleFromBlur}
              classNames={{
                wrapperClassName: "space-y-0.5 w-full",
                labelClassName: "text-center text-xs text-dark-600",
                inputClassName: "text-sm h-10 px-3 text-dark",
              }}
            />
            <TextInput
              label={t({ id: "common.to", defaultMessage: "To" })}
              classNames={{
                wrapperClassName: "space-y-0.5 w-full",
                labelClassName: "text-center text-xs text-dark-600",
                inputClassName: "text-sm h-10 px-3 text-dark",
              }}
              value={toDisplayValue}
              onChange={(e) => setToDisplayValue(e.target.value)}
              onBlur={handleToBlur}
            />
            <TextInput
              label={t({ id: "common.total", defaultMessage: "Total" })}
              value={totalDisplayValue}
              classNames={{
                wrapperClassName: "space-y-0.5 w-full",
                labelClassName: "text-center text-xs text-dark-600",
                inputClassName: "text-sm h-10 px-3 text-dark",
              }}
              onChange={(e) => setTotalDisplayValue(e.target.value)}
              onBlur={handleTotalBlur}
            />
          </div>
          {/* Notes */}
          <Controller
            control={control}
            name="notes"
            render={({ field }) => (
              <TextAreaInput
                placeholder={t({
                  id: "tasks-page.add-notes",
                  defaultMessage: "Add notes",
                })}
                maxLength={MAX_NOTES_LENGTH}
                lengthIndicator={{ max: MAX_NOTES_LENGTH }}
                classNames={{
                  inputClassName: cx(
                    "text-sm text-dark max-h-[300px]",
                    !isMobile && "min-h-[100px]"
                  ),
                }}
                {...field}
              />
            )}
          />
        </div>
      </form>
    );
  }
);
